// The front page: start a room or join one, the rooms this browser has been in, and the games.
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { generateLobbySlug } from '../utils/lobbyUtils';
import { READY_GAMES, DEV_GAMES, gameName, gameLetter, playersText } from './site/games';
import { loadRecentRooms, forgetRoom, timeAgo } from './site/recentRooms';
import type { RecentRoom } from './site/recentRooms';
import { heldLeagues, forgetLeague, getLeague, playerName } from '../leagues/api';
import type { LeagueView } from '../leagues/api';
import './site/site.css';

const PLAYER_NAME_KEY = 'player-name';
const REQUESTS_URL = 'https://docs.google.com/spreadsheets/d/1-i9-0fwq5D19Z82_pFol3ZvRJe1xHbQOeP0avzCx6Xo/edit?usp=drivesdk';
const BUG_TRACKER_URL = 'https://docs.google.com/spreadsheets/d/1-HMDnvSZb4nph4F-uWe18YTNxxpzzkGNKkd4kIPa210/edit?usp=drivesdk';

// A pasted room link (…/room/<slug>?…) or a bare code, as a room slug.
export function roomSlugFrom(text: string): string {
  const t = text.trim();
  const m = t.match(/\/room\/([^/?#\s]+)/);
  return (m ? decodeURIComponent(m[1]) : t).trim().toLowerCase();
}

function roomLine(r: RecentRoom): string {
  const others = r.players;
  const who = others.length === 0 ? '' : others.length <= 2 ? ` with ${others.join(' and ')}` : ` with ${others.length} others`;
  return `${gameName(r.gameId)}${who}`;
}

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [name, setName] = useState(() => localStorage.getItem(PLAYER_NAME_KEY) ?? '');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  const [code, setCode] = useState('');
  const [recent, setRecent] = useState<RecentRoom[]>(loadRecentRooms);
  const [leagues, setLeagues] = useState(heldLeagues);
  // Each league's state, fetched when the page opens, so an ongoing league looks different from a
  // finished one (and says when something is waiting for you).
  const [leagueState, setLeagueState] = useState<Record<string, LeagueView>>({});
  useEffect(() => {
    const live = { on: true };
    leagues.forEach(l => getLeague(l.id).then(L => { if (live.on) setLeagueState(m => ({ ...m, [l.id]: L })); }).catch(() => undefined));
    return () => { live.on = false; };
  }, [leagues]);
  const [missing, setMissing] = useState<string | null>(null);   // a code with no room behind it

  const saveName = () => {
    const n = draft.trim();
    if (n) { localStorage.setItem(PLAYER_NAME_KEY, n); setName(n); }
    setEditing(false);
  };
  // Check the room is there first: a typo shouldn't quietly open a new, empty room.
  const join = async () => {
    const slug = roomSlugFrom(code);
    if (!slug) return;
    try {
      const r = await fetch(`/api/rooms/${encodeURIComponent(slug)}`);
      const info = r.ok ? await r.json() : { exists: true };
      if (!info.exists) { setMissing(slug); return; }
    } catch { /* can't check: go ahead */ }
    navigate(`/room/${slug}`);
  };

  return (
    <div className="site" style={{ fontSize: 16 }}>
      <header style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '16px 24px', maxWidth: 1240, margin: '0 auto' }}>
        <span className="site-display" style={{ fontSize: 24, color: 'var(--accent)' }}>1001 Game Nights</span>
        {editing ? (
          <form onSubmit={e => { e.preventDefault(); saveName(); }} style={{ display: 'flex', gap: 8 }}>
            <input aria-label="Your name" value={draft} maxLength={30} autoFocus onChange={e => setDraft(e.target.value)} onKeyDown={e => e.key === 'Escape' && setEditing(false)} style={{ width: 160 }} />
            <button type="submit" className="site-btn site-btn-small">Save</button>
          </form>
        ) : (
          <span style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--muted)', fontSize: 15 }}>
            {name ? <>Playing as <strong style={{ color: 'var(--ink)' }}>{name}</strong></> : 'No name yet'}
            <button type="button" className="site-btn site-btn-small" onClick={() => { setDraft(name); setEditing(true); }}>{name ? 'Change name' : 'Choose a name'}</button>
          </span>
        )}
      </header>

      <main style={{ maxWidth: 1240, margin: '0 auto', padding: '8px 24px 48px', display: 'flex', flexDirection: 'column', gap: 40 }}>
        <section aria-labelledby="hero" className="landing-hero">
          <div className="landing-hero-text">
            <h1 id="hero">Game night is one link away</h1>
            <p style={{ fontSize: 19, color: '#e8dfd2', maxWidth: 520 }}>Original and classic board games for friends, in the browser. Start a room, paste the link, and play.</p>
            <div className="landing-hero-actions" style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              <button type="button" className="site-btn site-btn-primary site-btn-big" onClick={() => navigate(`/room/${generateLobbySlug()}`)}>Create a room</button>
              <button type="button" className="site-btn site-btn-big site-btn-outline" onClick={() => navigate('/leagues/new')}>Start a league</button>
            </div>
            <form onSubmit={e => { e.preventDefault(); join(); }} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end', maxWidth: 520 }}>
              <label style={{ flex: '1 1 260px', display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14, color: 'var(--muted)' }}>Got a link from a friend?
                <input type="text" value={code} onChange={e => { setCode(e.target.value); setMissing(null); }} placeholder="Paste the room link or code" style={{ minHeight: 48, borderRadius: 10, background: 'var(--panel)', padding: '0 14px', fontSize: 16 }} />
              </label>
              <button type="submit" className="site-btn" disabled={!roomSlugFrom(code)} style={{ minHeight: 48, background: 'var(--field)' }}>Join</button>
            </form>
            {missing && (
              <p role="alert" style={{ maxWidth: 520, fontSize: 15, color: '#f2d48f' }}>
                There's no room called <strong>{missing}</strong> right now. Check the spelling with whoever sent it,
                or <button type="button" className="site-btn site-btn-small" style={{ verticalAlign: 'baseline', minHeight: 30 }} onClick={() => navigate(`/room/${missing}`)}>open a new room called {missing}</button>
              </p>
            )}
          </div>
          <figure className="landing-hero-art">
            <img src="/assets/hero-crop.jpg" alt="A rooster gladiator in a chariot pulled by dice creatures, beside a dice factory" />
          </figure>
        </section>

        {(recent.length > 0 || leagues.length > 0) && (
          <section aria-labelledby="recent" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <h2 id="recent" className="site-display" style={{ fontSize: 26 }}>{leagues.length ? 'Your rooms and leagues' : 'Your rooms'}</h2>
            <div className="landing-cards">
              {[...leagues].sort((a, b) => Number(leagueState[a.id]?.status === 'done') - Number(leagueState[b.id]?.status === 'done')).map(l => {
                const st = leagueSummary(leagueState[l.id]);
                return (
                <article key={l.id} className="site-panel" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 6, position: 'relative', ...(st.done ? { opacity: 0.7 } : st.todo ? { borderColor: 'var(--accent)' } : {}) }}>
                  <button type="button" className="site-icon-btn" aria-label={`Remove ${l.name} from this list`} style={{ position: 'absolute', top: 8, right: 8, width: 32, height: 32 }}
                    onClick={() => { if (window.confirm(`Take ${l.name} off this list? This browser forgets your seat; you'd need your recovery code to get back in.`)) { forgetLeague(l.id); setLeagues(heldLeagues()); } }}>✕</button>
                  <span className="site-kicker" style={{ color: st.done ? 'var(--muted)' : 'var(--accent-soft)' }}>{st.kicker} · {timeAgo(l.at)}</span>
                  <strong style={{ overflowWrap: 'anywhere', paddingRight: 28 }}>{l.name}</strong>
                  <span style={{ fontSize: 14, color: st.todo ? 'var(--accent)' : 'var(--muted)', fontWeight: st.todo ? 600 : 400 }}>{st.line}</span>
                  <Link to={`/league/${l.id}`} style={{ marginTop: 4, fontWeight: 600 }}>{st.done ? 'See the results' : st.todo ? 'Go to the league' : 'Open league'}</Link>
                </article>
                );
              })}
              {recent.slice(0, 6).map(r => (
                <article key={r.slug} className="site-panel" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 6, position: 'relative' }}>
                  <button type="button" className="site-icon-btn" aria-label={`Remove ${r.slug} from this list`} style={{ position: 'absolute', top: 8, right: 8, width: 32, height: 32 }}
                    onClick={() => setRecent(forgetRoom(r.slug))}>✕</button>
                  <span className="site-kicker">Room · {timeAgo(r.at)}</span>
                  <strong style={{ overflowWrap: 'anywhere', paddingRight: 28 }}>{r.slug}</strong>
                  <span className="site-muted" style={{ fontSize: 14 }}>{roomLine(r)}</span>
                  <Link to={`/room/${r.slug}`} style={{ marginTop: 4, fontWeight: 600 }}>Rejoin</Link>
                </article>
              ))}
            </div>
            <p className="site-muted" style={{ fontSize: 14 }}>Remembered in this browser, like your name. Removing one only takes it off this list.</p>
          </section>
        )}

        <section aria-labelledby="games" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <h2 id="games" className="site-display" style={{ fontSize: 26 }}>The games</h2>
            <Link to="/explore" style={{ fontWeight: 600 }}>All games and their rules</Link>
          </div>
          <div className="landing-games">
            {READY_GAMES.map(g => (
              <Link key={g.id} to={`/games/${g.id}`} style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 64, padding: '10px 12px', borderRadius: 10, background: 'var(--panel)', border: '1px solid var(--panel-line)', color: 'var(--ink)', textDecoration: 'none' }}>
                <span className="site-letter" aria-hidden="true">{gameLetter(g)}</span>
                <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <span style={{ fontWeight: 600, fontSize: 14, lineHeight: 1.25 }}>{g.name}</span>
                  <span className="site-muted" style={{ fontSize: 12 }}>{playersText(g)}</span>
                </span>
              </Link>
            ))}
          </div>
          {DEV_GAMES.length > 0 && <>
            <h3 className="site-kicker" style={{ marginTop: 8 }}>In development <span className="site-muted" style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>· still being designed, rough edges</span></h3>
            <div className="landing-games" style={{ opacity: 0.75 }}>
            {DEV_GAMES.map(g => (
                <Link key={g.id} to={`/games/${g.id}`} style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 64, padding: '10px 12px', borderRadius: 10, background: 'var(--panel)', border: '1px solid var(--panel-line)', color: 'var(--ink)', textDecoration: 'none' }}>
                  <span className="site-letter" aria-hidden="true">{gameLetter(g)}</span>
                  <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <span style={{ fontWeight: 600, fontSize: 14, lineHeight: 1.25 }}>{g.name}</span>
                    <span className="site-muted" style={{ fontSize: 12 }}>{playersText(g)}</span>
                  </span>
                </Link>
              ))}
            </div>
          </>}
        </section>
      </main>

      <footer style={{ borderTop: '1px solid var(--panel-line)', padding: '18px 24px', display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px 24px', color: 'var(--muted)', fontSize: 14 }}>
        <a href={REQUESTS_URL} target="_blank" rel="noopener noreferrer">Suggest a feature</a>
        <a href={BUG_TRACKER_URL} target="_blank" rel="noopener noreferrer">Report a bug</a>
        <span>Made by John Kirschenheiter</span>
      </footer>
    </div>
  );
};

export default LandingPage;

// A league card's words: where the league is, and whether something is waiting for you.
function leagueSummary(L: LeagueView | undefined): { kicker: string; line: string; done: boolean; todo: boolean } {
  const card = (line: string, todo = false, done = false) => ({ kicker: done ? 'League · finished' : 'League', line, done, todo });
  if (!L) return card('Conquer the Flag league');
  const me = L.me ? L.players.find(p => p.id === L.me!.id) : undefined;
  if (L.status === 'open') return card(`Filling seats: ${L.players.length} of ${L.settings.seats} taken`);
  if (L.status === 'building') return me && !me.armyBuilt ? card('Armies: build yours', true) : card('Armies: waiting for the others');
  if (L.status === 'done') {
    const top = L.standings[0];
    return card(top ? `${playerName(L, top.id)} won the league` : 'Finished', false, true);
  }
  const round = `Round ${L.current + 1} of ${L.rounds.length}`;
  if (me && !L.me?.away) {
    if (me.pending > 0) return card(`${round} · choose your improvement`, true);
    const g = L.rounds[L.current]?.games.find(x => x.blue === me.id || x.red === me.id);
    if (g && !g.result && !g.voided) return card(`${round} · your game vs ${playerName(L, g.blue === me.id ? g.red : g.blue)}`, true);
  }
  return card(`${round} · waiting for the other games`);
}
